/**
 * Feeder bundle budget label math (FLY-5300).
 *
 * For Maldives charter bundle feeder flights, customer-facing prices on tiles and
 * upsell modal options render as the absolute hotel + flights package total, matching
 * every other bundled tile - never a per-leg figure or a `+/-$X` delta. The actual
 * checkout total is computed in `checkout/selectors/view/flights.ts`
 * (`getBudgetedSelectedDepartingLegPrice`, `getPendingReturningLegPrice`,
 * `getReturnFarePendingPrice`) and `client/lib/checkout/flights/cart.ts`
 * (`applyFeederBudgetToCost`); tile/modal previews call those same functions directly
 * so they can never drift from what the customer is actually charged. The functions
 * below are the shared low-level budget/buffer math those callers build on. Values are
 * aggregate across paying pax (matches `feederEstimatedPrice` and `price.all.totalFare`
 * conventions on `App.JourneyV2`).
 *
 * Rules:
 * - Departing leg: `0` excess when the fare sits inside `base + buffer`, otherwise
 *   `fare - base`. Never negative.
 * - Whole trip / returning leg: the fare (or round-trip fare) is snapped to `base` when
 *   within `base + buffer`, otherwise charged/shown as-is.
 */

export function computeDepartingFeederDelta({
  optionFare,
  baseBudget,
  bufferBudget = 0,
}: {
  optionFare: number
  baseBudget: number
  bufferBudget?: number
}): number {
  if (baseBudget <= 0) return 0
  if (optionFare <= baseBudget + bufferBudget) return 0
  const delta = optionFare - baseBudget
  return delta > 0 ? delta : 0
}

export function resolveDepartingFeederReference({
  bundleBudget,
  optionLegBudget,
  optionLegBuffer,
}: {
  bundleBudget: App.Checkout.FeederBundleBudget
  optionLegBudget?: number
  optionLegBuffer?: number
}): { baseBudget: number; bufferBudget: number } {
  if (optionLegBudget !== undefined && optionLegBudget > 0) {
    return {
      baseBudget: optionLegBudget,
      bufferBudget: optionLegBuffer ?? 0,
    }
  }
  if (bundleBudget.departingLegBudget && bundleBudget.departingLegBudget > 0) {
    return {
      baseBudget: bundleBudget.departingLegBudget,
      bufferBudget: bundleBudget.departingLegBuffer ?? 0,
    }
  }
  if (bundleBudget.returningLegBudget && bundleBudget.returningLegBudget > 0) {
    return {
      baseBudget: bundleBudget.returningLegBudget,
      bufferBudget: bundleBudget.returningLegBuffer ?? 0,
    }
  }
  return { baseBudget: 0, bufferBudget: 0 }
}

export function getBudgetedFeederLegFare(fare: number, legBudget: number, legBuffer: number = 0): number {
  if (fare <= legBudget + legBuffer) return legBudget
  return fare
}

export function getFirstLegFeederBudgetReference({
  optionBudget,
  legBudget,
  tripBudget,
}: {
  optionBudget?: number
  legBudget?: number
  tripBudget?: number
}): number {
  if (optionBudget !== undefined && optionBudget > 0) return optionBudget
  if (legBudget !== undefined && legBudget > 0) return legBudget
  if (tripBudget !== undefined && tripBudget > 0) return tripBudget
  return 0
}
